import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import content from "@/content";

const EASE = [0.22, 1, 0.36, 1];

export default function About() {
  const a = content.about;
  const imgs = a.images && a.images.length ? a.images : [a.portrait];
  const [imgIndex, setImgIndex] = useState(0);
  const stepImg = (d) => setImgIndex((p) => (p + d + imgs.length) % imgs.length);

  return (
    <section id="about" data-testid="about-section" className="relative py-16 lg:py-24 bg-blush texture-grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -inset-3 border border-gold/40 -translate-x-4 -translate-y-4" />
          <div className="relative overflow-hidden aspect-[4/5] group">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img
                key={imgs[imgIndex]}
                src={imgs[imgIndex]}
                alt={a.portraitCaption}
                data-testid="about-portrait"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </AnimatePresence>
            {imgs.length > 1 && (
              <div className="absolute bottom-4 right-4 flex items-center gap-2">
                <button
                  data-testid="about-prev-btn"
                  onClick={() => stepImg(-1)}
                  className="w-9 h-9 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors"
                  aria-label="Previous photo"
                >
                  <ArrowLeft size={14} />
                </button>
                <span className="bg-ink/60 backdrop-blur text-linen text-[10px] tracking-[0.2em] px-3 py-2 rounded-full">
                  {imgIndex + 1} / {imgs.length}
                </span>
                <button
                  data-testid="about-next-btn"
                  onClick={() => stepImg(1)}
                  className="w-9 h-9 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors"
                  aria-label="Next photo"
                >
                  <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif font-light text-4xl sm:text-5xl leading-tight mb-10"
            data-testid="about-heading"
          >
            {a.heading}
          </motion.h2>

          <div className="space-y-6 max-w-xl">
            {a.paragraphs.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.1 }}
                className="text-sm sm:text-base text-slate2 leading-relaxed"
              >
                {p}
              </motion.p>
            ))}
          </div>

          <motion.blockquote
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: EASE, delay: 0.3 }}
            className="mt-12 border-l-2 border-crimson pl-6 font-serif text-2xl sm:text-3xl italic text-ink"
            data-testid="about-pullquote"
          >
            “{a.pullQuote}”
          </motion.blockquote>
        </div>
      </div>
    </section>
  );
}
